import * as React from 'react';
import classnames from 'classnames';
import styles from './color-tile.module.scss';
import { RichText } from '../richtext';
import { Tooltip } from '../tooltip';

export type ColorTileProps = {
  id: string;
  primaryColor: string;
  secondaryColor?: string;
  titleText: string;
  selected?: boolean;
  disabled?: boolean;
  disabledText?: string;
  outlineClassName?: string;
} & Pick<JSX.IntrinsicElements['svg'], 'className' | 'aria-label' | 'role' | 'aria-hidden'>;

const baseClass = `color-tile`;

export const ColorTile: React.FC<ColorTileProps> = ({
  id,
  primaryColor,
  secondaryColor,
  titleText,
  outlineClassName,
  selected = false,
  disabled = false,
  disabledText,
  className,
  role,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden
}) => {
  const baseClasses = classnames(
    styles[baseClass],
    {
      [styles[`${baseClass}--disabled`] as string]: disabled
    },
    className
  );

  return (
    <>
      <svg
        id={id}
        className={baseClasses}
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        viewBox="0 0 40 40"
        fill="none"
        aria-label={ariaLabel}
        aria-hidden={ariaHidden}
        role={role}
      >
        <title>{titleText}</title>
        <clipPath id="cut-off-overflow">
          <circle cx="20.0001" cy="20.0001" r="16" clipRule="evenodd" />
        </clipPath>
        {selected && (
          <rect
            x="0.500003"
            y="-0.500003"
            width="39.0001"
            height="39.0001"
            rx="19.5001"
            transform="matrix(1 0 0 -1 0 39.0001)"
            strokeWidth="1.00001"
            className={outlineClassName || styles[`${baseClass}--selected`]}
          />
        )}
        <circle cx="19.9999" cy="20.3526" r="16.0001" transform="rotate(-90 19.9999 20.3526)" fill={primaryColor} />
        <path
          d="M4.00029 20.001C4.00029 17.8998 4.41415 15.8193 5.21823 13.878C6.02231 11.9368 7.20087 10.173 8.68661 8.68722C10.1724 7.20148 11.9362 6.02292 13.8774 5.21884C15.8186 4.41476 17.8992 4.0009 20.0004 4.0009C22.1016 4.0009 24.1821 4.41476 26.1234 5.21884C28.0646 6.02292 29.8284 7.20148 31.3142 8.68723C32.7999 10.173 33.9785 11.9368 34.7826 13.878C35.5866 15.8193 36.0005 17.8998 36.0005 20.001L20.0004 20.001L4.00029 20.001Z"
          fill={secondaryColor ?? primaryColor}
        />
        <circle cx="20.0001" cy="20.3001" r="15.6" stroke="var(--outline-color)" strokeOpacity="0.42" />
        {disabled && (
          <rect
            x="0"
            y="20"
            fill="white"
            width="100%"
            height="2"
            transform="rotate(-45 19.9999 20.3526)"
            clipPath="url(#cut-off-overflow)"
          />
        )}
      </svg>
      {disabled && disabledText && (
        <Tooltip
          className={styles[`${baseClass}--tooltip`]}
          id={id}
          text={<RichText mdString={disabledText} isLegal className={styles.richtext} />}
        />
      )}
    </>
  );
};
